import {
  HeadingGroup,
  ModalLayout,
  Pagination,
  SearchFilters,
  OffCanvasLayout,
  Table,
  ConfirmationBox,
} from "@wac-ui-dashboard/wac_component_library";
import useRemittance from "./useRemittance";
import EmptyData from "../../Global/EmptyData";
import OrderColumn from "../../Global/OrderColumn";
import { FaSort } from "react-icons/fa";
import CustomDateRangePicker from "../../Global/CustomDateRangePicker";
import Select from "react-select";
import NoteForm from "./NoteForm";
import ReasonModal from "../../Orders/orderListing/ReasonModal";
import RemittanceInvoice from "./Invoice";
import DashboardHero from "../../Dashboard/DashboardHero";

const RemittanceListing = ({ dashboard = false, subFilter = "" }) => {
  const {
    mainData,
    remittanceState,
    isFetching,
    isLoading,
    date,
    paginationOptions,
    actionOptions,
    showEditModal,
    newData,
    basicData,
    branchOptions,
    remittanceStatusOptions,
    OrderItems,
    handleSort,
    handleSearch,
    handlePagination,
    handlePageSize,
    handleDateRangeChange,
    handleBranchFilter,
    handleStatusFilter,
    handleClearClick,
    handleEditClick,
    handleActionChange,
    closeEditModal,
    handleCloseNoteForm,
    handleSubmitStatusComplete,
    closeStatusModal,
    handleSubmitPaymentStatusComplete,
    closePaymentStatusModal,
    setPaymentReview,
    closeInvoiceModal,
    getRow,
    refetch,
    updateTableFields,
  } = useRemittance({ subFilter });

  return (
    <>
      {!dashboard && (
        <HeadingGroup title={"Remittance"} className={`pro-mb-4`} />
      )}

      {!dashboard && (
        <div className={`pro-w-100 pro-my-5`}>
          <DashboardHero
            data={mainData?.data?.order_count_details}
            obj={OrderItems}
            transactions={true}
            loading={isFetching}
            shimmerCount={5}
            cardsPerLine={4}
            isOrder={true}
          />
        </div>
      )}

      <div className={`col-auto pro-pt-3 pro-pb-6`}>
        {!dashboard && (
        <div className="row">
          <div className="col">
            <SearchFilters
              dropDownFilter={
                <>
                  <div className="col-auto">
                    <CustomDateRangePicker
                      defaultDateRange={date}
                      handleDateRangeChange={handleDateRangeChange}
                    />
                  </div>

                  {remittanceStatusOptions?.length > 0 && (
                    <div className="col-auto">
                      <Select
                        id="status_filter"
                        isClearable={true}
                        placeholder={"Select Status"}
                        options={remittanceStatusOptions}
                        getOptionValue={(option) => option?.value}
                        getOptionLabel={(option) => option?.name}
                        className={`pro-input lg multi-select`}
                        classNamePrefix="pro-input"
                        value={
                          remittanceStatusOptions?.filter(
                            (p) =>
                              p?.value === remittanceState?.currentStatusFilter,
                          ) || null
                        }
                        onChange={(value) => handleStatusFilter(value)}
                      />
                    </div>
                  )}

                  <div className="col-auto">
                    <Select
                      id="branch"
                      isMulti
                      isClearable={true}
                      placeholder={"Select Branch"}
                      options={branchOptions}
                      getOptionValue={(option) => option?._id}
                      getOptionLabel={(option) => option?.name}
                      className={`pro-input lg multi-select`}
                      classNamePrefix="pro-input"
                      value={branchOptions?.filter((p) =>
                        remittanceState?.currentBranchFilter?.includes(p._id),
                      )}
                      onChange={(value) =>
                        handleBranchFilter(value.map((v) => v._id))
                      }
                    />
                  </div>
                </>
              }
              initialDateRange={date}
              onSearchInput={handleSearch}
              showActions={false}
              handleActionClick={handleEditClick}
              loading={isLoading}
              SearchIcon={
                <span className="material-symbols-outlined"> search </span>
              }
              actionOptions={actionOptions?.map((item) => item)}
              onActionOptionChange={handleActionChange}
              handleClear={handleClearClick}
              searchInputProps={{ value: remittanceState?.search }}
            />
          </div>
        </div>
        )}

        {mainData?.data?.data?.length === 0 ? (
          <EmptyData />
        ) : (
          <div className="pro-pt-3">
            <Table
              data={mainData?.data?.data || []}
              uniqueID={"id"}
              clear={false}
              multiSelect={false}
              assignable={false}
              fields={newData}
              SortIcon={<FaSort />}
              handleSort={handleSort}
              getRow={getRow}
              loading={isFetching}
              perpage={remittanceState?.currentPageSize}
              deletable={false}
              showCheckBox={false}
            />
          </div>
        )}

        {mainData?.data?.data?.length > 0 && !dashboard && (
          <Pagination
            currentPage={remittanceState?.currentPage}
            totalPageCount={Math.ceil(
              mainData?.data?.total_count / remittanceState?.currentPageSize,
            )}
            defaultValue={paginationOptions?.filter(
              (item) => item.value === remittanceState?.currentPageSize,
            )}
            onPageChange={handlePagination}
            options={paginationOptions}
            onActionChange={handlePageSize}
            center
          />
        )}

        <ModalLayout
          show={showEditModal}
          handleClose={closeEditModal}
          backdrop="static"
        >
          <div className="pro-m-5">
            <OrderColumn
              title={"Choose which columns you see"}
              refetch={refetch}
              tableFields={Object.keys(mainData?.data?.fields ?? {}).reduce(
                (filteredObj, property) => {
                  filteredObj[property] = mainData?.data?.fields[property];
                  return filteredObj;
                },
                {},
              )}
              moduleId={mainData?.data?.module_id}
              updateData={updateTableFields}
            />
          </div>
        </ModalLayout>

        <ModalLayout
          show={remittanceState?.showStatusModal}
          handleClose={closeStatusModal}
          backdrop="static"
          centered={true}
        >
          <div className="pro-m-5">
            <ConfirmationBox
              title={`Are you sure you want to complete this remittance?`}
              subTitle={`This action cannot be undone.`}
              isRight={true}
              cancelText={`No`}
              submitText={`Yes`}
              cancelAction={closeStatusModal}
              submitAction={handleSubmitStatusComplete}
            />
          </div>
        </ModalLayout>

        <ModalLayout
          show={remittanceState?.showPaymentStatusModal}
          handleClose={closePaymentStatusModal}
          backdrop="static"
        >
          <div className="pro-m-5">
            <ReasonModal
              setStatusReason={setPaymentReview}
              statusReason={remittanceState?.paymentReview}
              handleSubmitPaymentReviewChange={handleSubmitPaymentStatusComplete}
              closeModal={closePaymentStatusModal}
              errors={remittanceState?.paymentReviewError}
            />
          </div>
        </ModalLayout>

        <ModalLayout
          show={remittanceState?.showInvoiceModal}
          handleClose={closeInvoiceModal}
          centered={false}
        >
          <RemittanceInvoice
            remittanceId={remittanceState?.invoiceRemittanceId}
          />
        </ModalLayout>

        <OffCanvasLayout
          centered={true}
          show={remittanceState?.showNoteForm}
          handleClose={handleCloseNoteForm}
          title={"Note"}
          closeIcon={<span className="material-symbols-outlined">close</span>}
        >
          <NoteForm
            closeModal={handleCloseNoteForm}
            remittanceNoteId={remittanceState?.remittanceNoteId}
            onSuccess={refetch}
          />
        </OffCanvasLayout>
      </div>
    </>
  );
};

export default RemittanceListing;
